<template>
  <div
    :class="[
      isEmbedded
        ? 'flex w-full flex-col'
        : 'bp1680:w-[1054px] mx-auto flex w-full flex-col px-5 md:w-182 md:px-0 lg:w-195 xl:w-[893px] 2xl:w-[1054px]',
      getWrapperClass(),
    ]"
  >
    <h2
      v-if="resolveTitle(heading)"
      :class="['mb-5 text-center text-1.5xl/[115%] xl:mb-10 xl:text-xl/[120%]']"
    >
      {{ resolveTitle(heading) }}
    </h2>

    <div
      v-for="item in normalizedItems"
      :key="item.index"
      v-bind="item.isSingleMode ? attrs : {}"
      :class="[
        'accordion flex w-full flex-col border-t border-gray pb-2.5',
        isLastItem(item) ? 'border-b!' : '',
        getItemClass(item.index),
      ]"
    >
      <button
        @click="toggleAccordion(item.index)"
        class="group flex cursor-pointer items-center gap-[15px] md:gap-8 lg:gap-[51px] xl:gap-[45px] 2xl:gap-[67px]"
        :aria-label="`Accordion ${item.title}`"
        :aria-expanded="isItemOpen(item.index)"
        :aria-controls="`content-${uniqueId}-${item.index}`"
      >
        <svg
          viewBox="0 0 30 30"
          class="h-7.5 w-7.5 shrink-0"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <mask :id="`mask-${uniqueId}-${item.index}`">
              <rect width="30" height="30" rx="15" fill="white" />

              <g
                :style="{
                  transformOrigin: '15px 15px',
                  transform: isItemOpen(item.index)
                    ? 'rotate(90deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
                }"
              >
                <rect x="14" y="7" width="2" height="16" fill="black" />

                <rect
                  x="7"
                  y="14"
                  width="16"
                  height="2"
                  fill="black"
                  :style="{
                    transformOrigin: '15px 15px',
                    transform: isItemOpen(item.index) ? 'scale(0)' : 'scale(1)',
                    opacity: isItemOpen(item.index) ? 0 : 1,
                    transition:
                      'transform 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.4s cubic-bezier(0.4,0,0.2,1)',
                  }"
                />
              </g>
            </mask>
          </defs>
          <circle
            cx="15"
            cy="15"
            r="15"
            fill="black"
            :mask="`url(#mask-${uniqueId}-${item.index})`"
          />
        </svg>

        <span
          class="easy-in-out bp1680-text-47 text-left text-xl/[120%] transition-all duration-100 group-hover:translate-x-2.5 md:text-2.5xl/[108%] xl:text-4.5xl/[105%]"
        >
          {{ item.title }}
        </span>
      </button>

      <div
        :id="`content-${uniqueId}-${item.index}`"
        :class="[
          'grid transition-all duration-400 ease-in-out',
          isItemOpen(item.index)
            ? 'grid-rows-[1fr] xl:mt-10 xl:mb-15 2xl:mb-17.5'
            : 'mt-0 mb-0 grid-rows-[0fr] xl:mt-0 xl:mb-0',
        ]"
      >
        <div class="flex min-h-0 flex-col overflow-hidden">
          <div
            class="flex w-full flex-col py-7.5 md:pb-12.5 md:pl-[62px] lg:pl-[81px] xl:py-0 xl:pl-[75px] 2xl:pl-[97px]"
          >
            <slot v-if="item.isSingleMode" />

            <slot
              v-else-if="item.content?.[locale]"
              :name="`item-${item.index}`"
            >
              <Wysiwyg :content="item.content?.[locale]!" class="m-0! px-0!" />
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AccordionItem, LocalizedString } from '~/types'

defineOptions({ inheritAttrs: false })

interface Props {
  heading?: LocalizedString | string
  title?: LocalizedString | string
  items?: AccordionItem[]
  background?: string
  groupPosition?: 'first' | 'item'
  isLast?: boolean
  isEmbedded?: boolean
}

const props = defineProps<Props>()
const attrs = useAttrs()
const { locale } = useI18n()

const openIndexes = ref<number[]>([])
const uniqueId = useId()

const resolveTitle = (title?: LocalizedString | string) => {
  if (!title) return ''
  if (typeof title === 'string') return title
  return title?.[locale.value] ?? ''
}

const getWrapperClass = () => {
  if (props.isEmbedded) return ''
  return 'mb-10 md:mb-15 xl:mb-17.5 2xl:mb-20'
}

const getItemClass = (itemIndex: number) => {
  if (itemIndex === 0 && props.groupPosition === 'first') {
    return 'pt-2.5 md:pt-2.5'
  }
  return 'pt-2.5 md:pt-3.75'
}

const isLastItem = (item: { index: number; isSingleMode: boolean }) => {
  if (!item.isSingleMode) {
    return item.index === normalizedItems.value.length - 1
  }
  return props.isLast === true
}

const normalizedItems = computed(() => {
  if (props.items && props.items.length > 0) {
    return props.items.map((item, index) => ({
      index,
      title: resolveTitle(item.title),
      content: item.content,
      isFirst:
        props.items!.length === 1
          ? index === 0
          : index === 0 && props.groupPosition === 'first',
      isSingleMode: false,
    }))
  }

  return [
    {
      index: 0,
      title: resolveTitle(props.title),
      content: undefined,
      isFirst: props.groupPosition === 'first',
      isSingleMode: true,
    },
  ]
})

const isItemOpen = (index: number) => openIndexes.value.includes(index)

const toggleAccordion = (index: number) => {
  const position = openIndexes.value.indexOf(index)
  if (position !== -1) {
    openIndexes.value.splice(position, 1)
  } else {
    openIndexes.value.push(index)
  }
}
</script>
